import { parseMutationErrors } from '@reverbdotcom/commons/src/parse_graphql_errors';
import {
  GooglePayManagerFields,
  core_apimessages_CheckoutLineItem_Type as CheckoutLineItemType,
  core_apimessages_Checkout_Status as CheckoutStatus,
  core_apimessages_Error as Error,
  core_apimessages_AddressEntry as AddressEntry,
  GooglePayFinalizeActionFields,
  core_apimessages_Checkout_Type as CheckoutType,
  core_apimessages_GuestCheckoutUserEntry as GuestCheckoutUserEntry,
} from '@reverbdotcom/commons/src/gql/graphql';
import { mapAddressToEntry } from '../../lib/addressEntryMapping';

/** <https://docs.adyen.com/payment-methods/google-pay> */
export const GATEWAY = 'adyen';

export const API_VERSION = {
  apiVersion: 2,
  apiVersionMinor: 0,
};

export type Checkout = GooglePayManagerFields.Fragment & { action?: GooglePayFinalizeActionFields.Fragment };

export type CheckoutResult =
  { type: 'success', checkout: Checkout } |
  { type: 'failure', errors: Error[] };

export async function withGooglePayCheckoutResult(
  mutation: () => Promise<Checkout>,
): Promise<CheckoutResult> {
  try {
    const checkout = await mutation();

    return { type: 'success', checkout };
  } catch (e) {
    const errors = parseMutationErrors(e);

    return { type: 'failure', errors };
  }
}

export function mapPartialAddressToEntry(address: google.payments.api.IntermediateAddress): AddressEntry {
  return {
    locality: address.locality,
    postalCode: address.postalCode,
    region: address.administrativeArea,
    countryCode: address.countryCode,
  };
}

export function mapPaymentDataAddressToEntry({ paymentData, checkout }: {
  paymentData: google.payments.api.PaymentData,
  checkout: Checkout,
}): AddressEntry {
  if (!paymentData.shippingAddress) {
    return mapAddressToEntry(checkout.shippingAddress);
  }

  const { shippingAddress } = paymentData;

  return {
    name: shippingAddress.name,
    streetAddress: shippingAddress.address1,
    extendedAddress: shippingAddress.address2,
    locality: shippingAddress.locality,
    postalCode: shippingAddress.postalCode,
    region: shippingAddress.administrativeArea,
    countryCode: shippingAddress.countryCode,
    phone: shippingAddress.phoneNumber,
  };
}

export function mapOptionalGuestEntry({ paymentData, checkout }: {
  paymentData: google.payments.api.PaymentData,
  checkout: Checkout,
}): GuestCheckoutUserEntry {
  if (checkout.type === CheckoutType.STANDARD) {
    return undefined;
  }

  if (!paymentData.shippingAddress) {
    const { guest } = checkout;

    return {
      email: guest.email,
      firstName: guest.firstName,
      lastName: guest.lastName,
    };
  }

  const { shippingAddress, email } = paymentData;

  const [firstName, ...remainder] = shippingAddress.name.split(' ');
  const lastName = remainder.join(' ') || PLACEHOLDER_LAST_NAME;

  return {
    email,
    firstName,
    lastName,
  };
}

export function mapDisplayItems(checkout: Checkout) {
  const displayItems: google.payments.api.DisplayItem[] = checkout.lineItems
    .map((lineItem) => ({
      type: LINE_ITEM_TYPE_TO_DISPLAY_ITEM[lineItem.type] || 'LINE_ITEM',
      price: lineItem.amount.amount,
      label: lineItem.label,
      status: 'FINAL',
    }));

  return displayItems;
}

export function mapToShippingError(errors: Error[]): google.payments.api.PaymentDataError {
  return {
    reason: 'OTHER_ERROR',
    message: errors[0].message,
    intent: 'SHIPPING_ADDRESS',
  };
}

export function mapToAuthValidationError(errors: Error[]): google.payments.api.PaymentDataError {
  return {
    reason: 'OTHER_ERROR',
    message: errors[0].message,
    intent: 'PAYMENT_AUTHORIZATION',
  };
}

export function mapToAuthError(checkout: Checkout): google.payments.api.PaymentDataError {
  if (!checkout) {
    return null;
  }

  const reason: google.payments.api.ErrorReason =
    CHECKOUT_STATUS_TO_ERROR_REASON[checkout.status] || 'OTHER_ERROR';

  return {
    intent: 'PAYMENT_AUTHORIZATION',
    message: checkout.paymentErrorMessage,
    reason,
  };
}

const PLACEHOLDER_LAST_NAME = 'Guest';

export const LINE_ITEM_TYPE_TO_DISPLAY_ITEM: Record<string, google.payments.api.DisplayItemType> = {
  [CheckoutLineItemType.AMOUNT_SHIPPING]: 'LINE_ITEM',
  [CheckoutLineItemType.AMOUNT_LISTING_SUBTOTAL]: 'LINE_ITEM',
  [CheckoutLineItemType.AMOUNT_TAX]: 'TAX',
  [CheckoutLineItemType.AMOUNT_CREDIT]: 'DISCOUNT',
} as const;

export const CHECKOUT_STATUS_TO_ERROR_REASON: Record<string, google.payments.api.ErrorReason> = {
  [CheckoutStatus.INVALID_SHIPPING_ADDRESS_FAILURE]: 'SHIPPING_ADDRESS_INVALID',
  [CheckoutStatus.PAYMENT_INFORMATION_FAILURE]: 'PAYMENT_DATA_INVALID',
  [CheckoutStatus.CHECKOUT_PROCESSING_FAILURE]: 'PAYMENT_DATA_INVALID',
};
